import {TimelineMarker} from '../store';
import {
  CellOfTreeNodes,
  ColumnOfTreeNodes,
  FlowEdge,
  FlowNode,
  RowOfTreeNodes,
  Tree,
  TreeNode,
} from './types';
import {buildNodesTree} from './tree';
import {auto_calculate_dates, normalize_workflow_nodes} from './auto_calculate_dates';
import {connect_orphaned_nodes} from './connect_orphan_nodes';
import {defaultEdge} from '../const';
type LayoutOptions = {
  columnWidth: number;
  columnPadding: number;
  rowHeight: number;
  rowPadding: number;
  nodeXGap: number;
};

const defaultLayoutOptions: LayoutOptions = {
  columnWidth: 150,
  columnPadding: 50,
  rowHeight: 40,
  rowPadding: 20,
  nodeXGap: 40,
};

///////////////////////////////////////////////////////
function col_id_of_node(node: FlowNode) {
  const date = new Date(
    node.data?.actual_end_date || node.data?.rescheduled_end_date || node.data?.initial_end_date,
  );
  const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  return key;
}

///////////////////////////////////////////////////////
function createTimelineMarkers(columns: ColumnOfTreeNodes[]) {
  const timelineMarkers: TimelineMarker[] = [];
  let currentX = 0;
  const markerPadding = defaultLayoutOptions.columnPadding;

  for (const column of columns) {
    const x = currentX;
    const width = column.width + 2 * markerPadding;

    const marker: TimelineMarker = {
      id: column.col_id,
      x,
      width,
      timestamp: new Date(column.col_id),
      label: column.col_id,
      extra: column,
    };
    timelineMarkers.push(marker);
    currentX += marker.width;
  }
  return timelineMarkers;
}

///////////////////////////////////////////////////////
export function createTimelineMarkersFromNodes(nodes: FlowNode[]) {
  const markers: TimelineMarker[] = [];
  let currentX = 0;
  for (const node of nodes) {
    const col_id = col_id_of_node(node);
    const x = currentX;
    if (markers.find(m => m.id === col_id)) continue;

    const marker: TimelineMarker = {
      id: col_id,
      timestamp: new Date(col_id),
      label: col_id,
      x,
      width: 200,
    };
    markers.push(marker);
    currentX += marker.width;
  }
  return markers;
}

///////////////////////////////////////////////////////
function layoutTreeNodes(tree: Tree): {
  rows: RowOfTreeNodes[];
  columns: ColumnOfTreeNodes[];
  cells: CellOfTreeNodes[];
  newNodes: FlowNode[];
} {
  const {rowPadding, columnPadding, nodeXGap, rowHeight, columnWidth} = defaultLayoutOptions;

  const rows: RowOfTreeNodes[] = [];
  const columns: ColumnOfTreeNodes[] = [];

  function processNode(tree_node: TreeNode, row: RowOfTreeNodes) {
    const flowNode = tree_node.node;
    const col_id = col_id_of_node(flowNode);

    // Store original Y position
    const originalY = flowNode.position.y;

    // Add to new nodes
    const newNode = {...flowNode, position: {x: 0, y: originalY}};
    tree_node.node = newNode;

    // Assign TreeNode properties
    tree_node.col_id = col_id;
    tree_node.rowIndex = row.rowIndex;

    // Add current node to row
    row.tree_nodes.push(tree_node);
    row.height = Math.max(row.height, flowNode.measured?.height ?? rowHeight);

    ///// only with non-start node
    if (flowNode.type != 'start') {
      // Add current node to cell of row
      let cell = row.cells.find(cell => cell.col_id === col_id);
      if (!cell) {
        cell = {
          rowIndex: row.rowIndex,
          col_id,
          tree_nodes: [],
          width: 0,
          height: rowHeight,
        };
        row.cells.push(cell);
      }

      // Add current cell to column
      let column = columns.find(col => col.col_id === col_id);
      if (!column) {
        column = {
          col_id,
          tree_nodes: [],
          cells: [],
          width: 0,
          x: 0,
        };
        columns.push(column);
      }
      column.tree_nodes.push(tree_node);
      const cell_in_column = column.cells.find(cell => cell.rowIndex === row.rowIndex);
      if (!cell_in_column) column.cells.push(cell);

      // update cell nodes
      cell.tree_nodes.push(tree_node);
      cell.width += flowNode.measured?.width ?? columnWidth;
      if (cell.tree_nodes.length > 1) cell.width += defaultLayoutOptions.nodeXGap;
      cell.height = Math.max(cell.height, flowNode.measured?.height ?? rowHeight);

      // Calculate node's position based on parent node (only X coordinate)
      const parent_node = tree_node.parents?.at(-1)?.node;
      if (parent_node) {
        const x = parent_node.position.x + (parent_node.measured?.width ?? columnWidth) + nodeXGap;
        newNode.position = {
          x,
          y: originalY, // Preserve original Y position
        };
      }
    }

    // Process children (without changing vertical position)
    for (let index = 0; index < tree_node.children.length; index++) {
      const child = tree_node.children[index];
      processNode(child, row); // Use the same row for all children to avoid vertical arrangement
    }
  }

  // Start processing from root
  if (tree.root) {
    const firstRow: RowOfTreeNodes = {
      tree_nodes: [],
      cells: [],
      rowIndex: 0,
      height: rowHeight,
      y: 0,
    };
    rows.push(firstRow);
    processNode(tree.root, firstRow);
  }

  // Collect columns and cells
  const cells = rows.flatMap(row => row.cells);
  // Sort columns by col_id
  columns.sort((a, b) => a.col_id.localeCompare(b.col_id));
  // Sort cells in each column by rowIndex
  columns.forEach(column => {
    column.cells.sort((a, b) => a.rowIndex - b.rowIndex);
  });

  // Calculate positions of nodes in columns
  let previous_column: ColumnOfTreeNodes | undefined = undefined;
  for (const column of columns) {
    const col_x = previous_column
      ? (previous_column.x || 0) + previous_column.width + 2 * columnPadding
      : 0;
    for (const cell of column.cells) {
      for (const tree_node of cell.tree_nodes) {
        const parent_tree_node = tree_node.parents?.[0];
        const parent_node = parent_tree_node?.node;
        const node = tree_node.node;

        const xByParent =
          parent_node && parent_node.type != 'start'
            ? parent_node?.position.x + (parent_node.measured?.width ?? columnWidth) + nodeXGap
            : 0;

        // Only update X coordinate, preserve Y
        node.position.x = Math.max(col_x + columnPadding, xByParent);
      }
      const last_node = cell.tree_nodes[cell.tree_nodes.length - 1].node;
      const first_node = cell.tree_nodes[0].node;
      cell.x = first_node.position.x;
      cell.width =
        last_node.position.x + (last_node.measured?.width ?? columnWidth) - first_node.position.x;
    }

    column.x = col_x;
    column.width = column.cells.reduce(
      (acc, cell) => Math.max(acc, (cell.x || 0) - col_x + cell.width),
      0,
    );
    previous_column = column;
  }

  // assign start_node position (only X coordinate)
  const newNodes = rows.flatMap(row => row.tree_nodes.map(tree_node => tree_node.node));
  const start_node = newNodes.find(n => n.type === 'start');
  if (start_node) {
    const originalY = start_node.position.y;
    start_node.position = {
      x: -(start_node.measured?.width || 150) - columnPadding,
      y: originalY, // Preserve original Y position
    };
  }

  return {
    rows,
    columns,
    cells,
    newNodes,
  };
}

///////////////////////////////////////////////////////
export function arrange_nodes_v2(
  _nodes: FlowNode[],
  _edges: FlowEdge[],
): {timelineMarkers: TimelineMarker[]; newNodes: FlowNode[]; newEdges: FlowEdge[]} {
  // Connect orphan nodes to the root
  const {nodes, edges} = normalize_workflow_nodes(_nodes, _edges);

  ///// build tree of nodes
  const tree = buildNodesTree(nodes, edges);
  if (!tree.root) return {timelineMarkers: [], newNodes: nodes, newEdges: edges};
  console.log('Tree:', tree);

  const {rows, columns, cells, newNodes} = layoutTreeNodes(tree);
  console.log('results', {rows, columns, cells, newNodes});

  return {
    timelineMarkers: createTimelineMarkers(columns),
    newNodes: newNodes,
    newEdges: edges,
  };
}
